import React, { useEffect, useState } from "react";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { getImageUrl, getProfileImageUrlByAccountTypeCode } from "helpers/Image.helper";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { fetchHomeStudentDiary, IFetchHomeStudentDiaryResponse } from "api/koach/v2/home.api";
import { toggleDiaryLike, deleteDiary, fetchDiaryLikes } from "api/koach/v2/diary.api";
import { IDiaryLike } from "api/interfaces/profile.interface";
import { usePopup } from "contexts/PopupContext";
import StudentDiaryDetailPopup from "pages/koach/v2/popup/StudentDiaryDetailPopup";
import LikePopupForDiary from "pages/koach/v2/popup/LikePopupForDiary";
import VideoPlayer from "commons/video/VideoPlayer";
import ReportPopup from "commons/popup/ReportPopup";
import { getStudentNickname } from "helpers/String.helper";

const KoachCoachingDiary = () => {
    const [diaryData, setDiaryData] = useState<IFetchHomeStudentDiaryResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [activeKebabMenu, setActiveKebabMenu] = useState<number | null>(null);
    const [showReportPopup, setShowReportPopup] = useState(false);
    const [diaryToReport, setDiaryToReport] = useState<{ id: string; title: string; content: string } | null>(null);
    const [likingDiaryIds, setLikingDiaryIds] = useState<Set<number>>(new Set()); // 좋아요 처리 중인 다이어리 ID들
    const [diaryLikesMap, setDiaryLikesMap] = useState<Map<number, IDiaryLike[]>>(new Map()); // 다이어리별 좋아요 목록
    const { showPopup } = usePopup();

    useEffect(() => {
        fetchDiaries();
    }, []);

    // 케밥 메뉴 외부 클릭 시 메뉴 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (activeKebabMenu !== null) {
                setActiveKebabMenu(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [activeKebabMenu]);

    // 무한 스크롤 이벤트 핸들러
    useEffect(() => {
        let isScrolling = false;

        const handleScroll = () => {
            if (isScrolling || isLoadingMore || !diaryData?.pagination?.hasNextPage) {
                return;
            }

            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;

            if (scrollTop + windowHeight >= documentHeight - 100) {
                isScrolling = true;
                const nextPage = diaryData.pagination.currentPage + 1;
                fetchDiaries(nextPage, true).finally(() => {
                    setTimeout(() => {
                        isScrolling = false;
                    }, 1000);
                });
            }
        };

        if (diaryData && diaryData.pagination) {
            window.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }
    }, [diaryData, isLoadingMore]);

    const fetchDiaries = async (page: number = 1, append: boolean = false) => {
        try {
            if (append) {
                setIsLoadingMore(true);
            } else {
                setIsLoading(true);
            }

            const result = await fetchHomeStudentDiary({ page });

            if (append && diaryData) {
                setDiaryData(prevData => {
                    const newData = {
                        ...result,
                        diaryList: [...prevData.diaryList, ...result.diaryList]
                    };
                    return newData;
                });
            } else {
                setDiaryData(result);
            }

            // API 응답에서 받은 좋아요 목록을 Map에 저장
            const newLikesMap = new Map<number, IDiaryLike[]>();
            if (result && result.diaryList && Array.isArray(result.diaryList)) {
                result.diaryList.forEach((diary: any) => {
                    if (diary && diary.likes && Array.isArray(diary.likes)) {
                        // likes 배열의 각 항목을 IDiaryLike 형식으로 변환
                        const likes = diary.likes.map((like: any) => ({
                            diaryId: diary.id,
                            userId: like.user?.id || like.userId || '',
                            createdAt: like.createdAt || new Date(),
                            isMylike: false,
                            user: like.user
                        }));
                        newLikesMap.set(diary.id, likes);
                    } else {
                        // 좋아요가 없으면 빈 배열로 설정
                        newLikesMap.set(diary.id, []);
                    }
                });
            }
            setDiaryLikesMap(prevMap => {
                const mergedMap = new Map(prevMap);
                newLikesMap.forEach((likes, diaryId) => {
                    mergedMap.set(diaryId, likes);
                });
                return mergedMap;
            });
        } catch (error) {
            console.error("지망생 다이어리 목록 조회 실패:", error);
            if (!append) {
                setDiaryData({ diaryList: [], pagination: { currentPage: 1, totalCount: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false } });
            }
        } finally {
            if (append) {
                setIsLoadingMore(false);
            } else {
                setIsLoading(false);
            }
        }
    };

    const handleDiaryClick = (diary: any) => {
        showPopup(
            <StudentDiaryDetailPopup
                diaryId={diary.id.toString()}
                onClose={() => {}}
            />
        );
    };

    const handleLikeCountClick = (diary: any, event: React.MouseEvent) => {
        event.stopPropagation();
        if (diary.likeCount && diary.likeCount > 0) {
            showPopup(
                <LikePopupForDiary
                    diaryId={diary.id.toString()}
                    onClose={() => showPopup(null)}
                />
            );
        }
    };

    const handleLikeToggle = async (diary: any, event: React.MouseEvent) => {
        event.stopPropagation();

        // 이미 좋아요 처리 중인 다이어리인지 확인
        if (likingDiaryIds.has(diary.id)) {
            console.log("이미 좋아요 처리 중인 다이어리:", diary.id);
            return;
        }

        try {
            // 좋아요 처리 중인 다이어리 ID 추가
            setLikingDiaryIds(prev => new Set(prev).add(diary.id));

            const result = await toggleDiaryLike(diary.id.toString());
            console.log("좋아요 토글 결과:", result);
            
            // 실시간으로 다이어리 데이터 업데이트
            setDiaryData(prevData => {
                if (!prevData) return prevData;
                const updatedList = prevData.diaryList.map(item => {
                    if (item.id === diary.id) {
                        const updatedItem = {
                            ...item,
                            isLiked: result.isLiked,
                            likeCount: result.likeCount || 0
                        };
                        console.log(`다이어리 ${diary.id} 업데이트:`, {
                            이전: { isLiked: item.isLiked, likeCount: item.likeCount },
                            이후: { isLiked: updatedItem.isLiked, likeCount: updatedItem.likeCount }
                        });
                        return updatedItem;
                    }
                    return item;
                });
                return {
                    ...prevData,
                    diaryList: updatedList
                };
            });

            // 좋아요 목록 새로고침 (좋아요 추가/제거 반영)
            try {
                const likesResult = await fetchDiaryLikes(diary.id.toString(), 1, 5);
                setDiaryLikesMap(prevMap => {
                    const newMap = new Map(prevMap);
                    newMap.set(diary.id, likesResult.likeList);
                    return newMap;
                });
            } catch (error) {
                console.error("좋아요 목록 새로고침 실패:", error);
            }
        } catch (error) {
            console.error("좋아요 토글 실패:", error);
        } finally {
            // 좋아요 처리 완료 후 ID 제거
            setLikingDiaryIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(diary.id);
                return newSet;
            });
        }
    };

    const handleDeleteDiary = async (diary: any, event: React.MouseEvent) => {
        event.stopPropagation();

        if (window.confirm("정말로 이 다이어리를 삭제하시겠습니까?")) {
            try {
                await deleteDiary(diary.id.toString());
                setDiaryData(prevData => {
                    if (!prevData) return prevData;
                    return {
                        ...prevData,
                        diaryList: prevData.diaryList.filter(item => item.id !== diary.id)
                    };
                });
            } catch (error) {
                console.error("다이어리 삭제 실패:", error);
                alert("다이어리 삭제에 실패했습니다.");
            }
        }
        setActiveKebabMenu(null);
    };

    const handleReportDiary = async (diary: any, event: React.MouseEvent) => {
        event.stopPropagation();
        setDiaryToReport({
            id: diary.id,
            title: diary.title || "제목 없음",
            content: diary.content || "내용 없음"
        });
        setShowReportPopup(true);
        setActiveKebabMenu(null);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 pt-20 pb-4 px-4">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-2xl shadow-sm p-4">
                        <div className="animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-1/4 mb-3"></div>
                            <div className="space-y-2">
                                <div className="h-16 bg-gray-200 rounded"></div>
                                <div className="h-16 bg-gray-200 rounded"></div>
                                <div className="h-16 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!diaryData || diaryData.diaryList.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 pt-20 pb-4 px-4">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-2xl shadow-sm p-4">
                        <h1 className="text-2xl font-bold text-gray-900 mb-3">성장 다이어리</h1>
                        <div className="text-center py-6">
                            <p className="text-gray-500">
                                {getLanguageValue(PAGE_TYPE.KOACH, "home.txtNoStudentDiaries") || "아직 작성된 다이어리가 없습니다."}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pt-20 pb-4 px-4">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-2xl font-bold text-gray-900 mb-6">성장 다이어리</h1>
                
                <div className="bg-white rounded-2xl shadow-sm p-4">
                    <div className="space-y-4">
                        {diaryData.diaryList.map((diary) => (
                            <div
                                key={diary.id}
                                className="border border-gray-200 rounded-xl p-3 hover:shadow-md transition-shadow cursor-pointer"
                                onClick={() => handleDiaryClick(diary)}
                            >
                                {/* 다이어리 헤더 */}
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-8 h-8 rounded-full overflow-hidden">
                                            <img
                                                src={getImageUrl(diary.user?.userStudent?.profileImage, 80, 80)}
                                                alt="Student Profile"
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = "/imgs/dummy/basic-img.png";
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900 text-sm">
                                                {getStudentNickname(diary.user?.userStudent, '')}
                                            </h3>
                                            <p className="text-xs text-gray-500">
                                                {formatTimeAgo(diary.createdAt)}
                                            </p>
                                        </div>
                                    </div>

                                    {/* 케밥 메뉴 버튼 */}
                                    <div className="relative">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveKebabMenu(activeKebabMenu === diary.id ? null : diary.id);
                                            }}
                                            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                            title="메뉴"
                                        >
                                            <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                            </svg>
                                        </button>

                                        {/* 케밥 메뉴 툴팁 */}
                                        {activeKebabMenu === diary.id && (
                                            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]">
                                                <button
                                                    onClick={(e) => handleReportDiary(diary, e)}
                                                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-2"
                                                >
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                                    </svg>
                                                    <span>신고</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* 다이어리 제목 */}
                                <h4 className="text-base font-medium text-gray-900 mb-1">
                                    {diary.title}
                                </h4>

                                {/* 다이어리 내용 */}
                                <p className="text-gray-700 mb-3 line-clamp-3 text-sm">
                                    {diary.content}
                                </p>

                                {/* 첨부된 비디오 */}
                                {diary.diaryVideos && diary.diaryVideos.length > 0 && (
                                    <div className="mb-3">
                                        {diary.diaryVideos.map((diaryVideo, index) => (
                                            <VideoPlayer
                                                key={index}
                                                video={diaryVideo.video}
                                                className="w-full rounded-lg"
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* 첨부된 이미지 */}
                                {diary.diaryImages && diary.diaryImages.length > 0 && (
                                    <div className="mb-3">
                                        <div className="flex gap-2 overflow-x-auto pb-2">
                                            {diary.diaryImages.map((diaryImage, index) => (
                                                <div key={index} className="relative flex-shrink-0">
                                                    <img
                                                        src={getImageUrl(diaryImage.image, 200, 200)}
                                                        alt={`Diary image ${index + 1}`}
                                                        className="w-20 h-20 object-cover rounded-lg"
                                                        onError={(e) => {
                                                            const target = e.target as HTMLImageElement;
                                                            target.src = "/imgs/dummy/basic-img.png";
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* 다이어리 메타 정보 영역 */}
                                <div className="flex flex-col gap-3 border-t border-gray-100 pt-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-6">
                                            <div
                                                className={`flex items-center space-x-2 transition-all duration-200 ${
                                                    likingDiaryIds.has(diary.id)
                                                        ? 'cursor-not-allowed opacity-50'
                                                        : 'cursor-pointer hover:opacity-80'
                                                }`}
                                                onClick={(e) => {
                                                    e.stopPropagation(); // 항상 이벤트 전파 방지
                                                    if (!likingDiaryIds.has(diary.id)) {
                                                        handleLikeToggle(diary, e);
                                                    }
                                                }}
                                            >
                                                {likingDiaryIds.has(diary.id) ? (
                                                    <svg
                                                        className="w-5 h-5 text-gray-400 animate-pulse"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        className={`w-5 h-5 ${diary.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}`}
                                                        fill={diary.isLiked ? '#CF3688' : 'none'}
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                    </svg>
                                                )}
                                                <span
                                                    className={`text-sm font-medium ${diary.isLiked ? 'text-[#CF3688]' : 'text-gray-500'} ${(diary.likeCount && diary.likeCount > 0) ? 'cursor-pointer' : ''}`}
                                                    onClick={(e) => handleLikeCountClick(diary, e)}
                                                >
                                                    {diary.likeCount || 0}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-2 text-gray-500 hover:text-[#CF3688] transition-colors duration-200 cursor-pointer">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                </svg>
                                                <span className="text-sm font-medium">
                                                    {diary.commentCount || 0}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 좋아요 목록 영역 */}
                                    {(() => {
                                        const likes = diaryLikesMap.get(diary.id) || [];
                                        const likeCount = diary.likeCount || 0;

                                        if (likeCount === 0) {
                                            return (
                                                <div className="pt-2 text-center">
                                                    <p className="text-xs text-gray-400">제일 처음 좋아요를 눌러보세요</p>
                                                </div>
                                            );
                                        }

                                        if (likes.length === 0) {
                                            return null; // 로딩 중이거나 데이터가 없을 때
                                        }

                                        const displayLikes = likes.slice(0, 5);
                                        const remainingCount = likeCount - displayLikes.length;

                                        return (
                                            <div 
                                                className="pt-2 cursor-pointer hover:bg-gray-50 rounded-lg p-2 -mx-2 transition-colors"
                                                onClick={(e) => handleLikeCountClick(diary, e)}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <div className="flex -space-x-2">
                                                        {displayLikes.map((like, index) => (
                                                            <div
                                                                key={like.userId}
                                                                className="w-6 h-6 rounded-full border-2 border-white overflow-hidden shadow-sm bg-gray-100"
                                                                style={{ zIndex: 5 - index }}
                                                            >
                                                                <img
                                                                    src={getProfileImageUrlByAccountTypeCode(like?.user, 32, 32) || "/imgs/dummy/basic-img.png"}
                                                                    alt={getStudentNickname(like?.user?.userStudent, like?.user?.username || '') || "User"}
                                                                    className="w-full h-full object-cover"
                                                                    onError={(e) => {
                                                                        const target = e.target as HTMLImageElement;
                                                                        target.src = "/imgs/dummy/basic-img.png";
                                                                    }}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <span className="text-xs text-gray-600 whitespace-nowrap flex-shrink-0">
                                                        {diary.likeCount || 0}명이 좋아요를 눌렀어요❤️
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        ))}

                        {/* 추가 로딩 상태 */}
                        {isLoadingMore && (
                            <div className="text-center py-4">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                <span className="text-gray-500 text-sm">더 많은 다이어리를 불러오는 중...</span>
                            </div>
                        )}

                        {/* 더 이상 로드할 데이터가 없을 때 */}
                        {!isLoadingMore && diaryData.pagination.hasNextPage === false && diaryData.diaryList.length > 0 && (
                            <div className="text-center py-4">
                                <span className="text-gray-400 text-sm">모든 다이어리를 불러왔습니다.</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* 다이어리 신고 팝업 */}
                {showReportPopup && diaryToReport && (
                    <ReportPopup
                        type="diary"
                        diaryId={diaryToReport.id}
                        content={`${diaryToReport.title}\n\n${diaryToReport.content}`}
                        onClose={() => {
                            setShowReportPopup(false);
                            setDiaryToReport(null);
                        }}
                        onSuccess={() => {
                            console.log('다이어리 신고가 완료되었습니다.');
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default KoachCoachingDiary;

